import { Link, useHistory } from 'react-router-dom'
import { QrScannerExpanded } from '../../components/QRScannerExpanded'
import SplashScreen from '../../components/SplashScreen'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useAPI } from '../../api/api'

import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-short-left.svg'
import { ReactComponent as ScanIcon } from '../../icons/qr_code.svg'
import { ReactComponent as ListIcon } from '../../icons/list.svg'
import { ReactComponent as BinIcon } from '../../icons/bin.svg'
import { ReactComponent as ArrowRightIcon } from '../../icons/arrow-right.svg'
import FailImage from '../../images/fail.png'
import { parseItemQrCode } from '../../api/utils'

type ItemData = {
  _id: string
  uuid: string
  itemTypeName: string
  itemTypeId: string
  iconUrl: string
  client: any
  merchant: any
  status: string
  serialNumber?: string | null
}

type ItemListData = {
  _id: string
  itemTypeIcon: string
  items: ItemData[]
}

const ReturnToInventory = () => {
  let { t } = useTranslation(['translation', 'errors'])
  let history = useHistory()

  let { errors, wrappedExtendedQueryAuth, wrappedExtendedMutateAuth } = useAPI()

  const [isLoading, setIsLoading] = useState(true)
  const [isScanning, setIsScanning] = useState('false')
  const [activeTab, setActiveTab] = useState(1)
  const [scannerActive, setScannerActive] = useState(true)
  const [scannedItems, setScannedItems] = useState<ItemData[]>([])
  const [itemListByType, setItemListByType] = useState<ItemListData[]>([])
  const [itemTypeExpanded, setItemTypeExpanded] = useState<ItemData[]>([])
  const [lastScannedItem, setLastScannedItem] = useState<ItemData | undefined>()

  const [showRemoveScanner, setShowRemoveScanner] = useState(false)
  const [showReturnOverview, setShowReturnOverview] = useState(false)

  const [failedItems, setFailedItems] = useState<string[]>([])
  const [showFailedToReturnItems, setShowFailedToReturnItems] = useState(false)

  const sleep = (ms: any) => new Promise(r => setTimeout(r, ms))

  const [ended, setEnded] = useState(false)

  useEffect(() => {
    async function onLoad() {
      await sleep(1000)
      setIsLoading(false)
    }
    onLoad().then(() => setIsLoading(false))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onQrCode = async (code: string) => {
    onQrCodeRead(code).then(() => setIsScanning('false'))
  }

  const onQrCodeRead = async (code: string) => {
    setLastScannedItem(undefined)

    const qr = parseItemQrCode(code)
    if (!qr) {
      toast.error(t('invalid-item-qr-code'))
      return
    }

    const query = `query { itemsById (filter : {uuid : "${qr}"}) { _id uuid serialNumber status itemType { _id name iconUrl } client { boId firstName lastName } merchant { _id boId name }}}`
    const factory = (result: any): ItemData => {
      return Array.isArray(result.itemsById) && result.itemsById.length > 0
        ? result.itemsById.map((o: any) => ({
            _id: o._id,
            itemTypeName: o.itemType.name,
            itemTypeId: o.itemType._id,
            iconUrl: o.itemType.iconUrl,
            uuid: o.uuid,
            serialNumber: o.serialNumber || null,
            status: o.status,
            client: o.client,
            merchant: o.merchant ? o.merchant.name : null,
          }))[0]
        : null
    }

    const item = await wrappedExtendedQueryAuth(query, factory)

    if (!item) {
      toast.error(t('item-does-not-exist'))
      return
    }

    setLastScannedItem(item)
  }

  useEffect(() => {
    if (!lastScannedItem) return
    const alreadyExist = scannedItems.find(q => q._id === lastScannedItem._id)
    if (alreadyExist) {
      toast.warning(t('item-aready-added'))
      return
    }

    toast.success(`${lastScannedItem.itemTypeName} ${t('added-successfully')}`)

    addToList(lastScannedItem)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastScannedItem])

  const addToList = async (data: any) => {
    let index = itemListByType.findIndex(r => r._id === data.itemTypeId)
    if (index < 0) {
      setItemListByType(itemListByType => {
        return [...itemListByType, { _id: data.itemTypeId, itemTypeIcon: data.iconUrl, items: [data] }]
      })
      setScannedItems(scannedItems => {
        return [...scannedItems, data]
      })
    } else {
      let itemIndex = itemListByType[index].items.findIndex(i => i._id === data._id)
      if (itemIndex < 0) {
        const tempItemList = itemListByType
        tempItemList[index].items.push(data)
        setItemListByType(tempItemList)
        setScannedItems(scannedItems => [...scannedItems, data])
      } else {
        toast.error(t('item-aready-added'))
      }
    }
  }

  useEffect(() => {
    const onErrors = () => {
      if (ended) {
        if (errors.length > 0) {
          errors.forEach(error => {
            toast.error(t(error.msg, { ns: 'errors' }))
          })
        }
      }
    }
    return () => {
      onErrors()
      setEnded(false)
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ended])

  const onCancel = () => {
    history.push('/')
  }

  const onClickScanner = () => {
    setActiveTab(1)
    setScannerActive(true)
  }

  const onClickList = () => {
    setActiveTab(2)
    setScannerActive(false)
  }

  const onHome = () => {
    history.push('/merchant/inventory')
  }

  const onItemTypeClick = (items: ItemData[]) => {
    if (items.length > 0) {
      setItemTypeExpanded(items)
    }
  }

  const onScanToRemove = () => {
    setShowRemoveScanner(true)
  }

  const onScanToRemoveQRCode = async (code: string) => {
    const uuid = parseItemQrCode(code)
    if (!uuid) {
      toast.error(t('invalid-item-qr-code'))
      return
    }
    const index = scannedItems.findIndex(r => r.uuid === uuid)
    if (index < 0) {
      toast.error(t('item-not-added'))
      return
    }

    let tempType = ''

    setScannedItems(
      scannedItems.filter(item => {
        if (item.uuid === uuid) tempType = item.itemTypeName
        return item.uuid !== uuid
      }),
    )

    let tempItemListByType: ItemListData[] = []

    for (const type of itemListByType) {
      const y = type.items.filter(item => item.uuid !== uuid)
      tempItemListByType.push({ _id: type._id, itemTypeIcon: type.itemTypeIcon, items: y })
    }

    toast.info(`${tempType} ${t('removed-from-list')}`)
    setItemListByType(tempItemListByType)
  }

  const onClickToRemove = async (uuid: string) => {
    let tempType = ''

    setScannedItems(
      scannedItems.filter(item => {
        if (item.uuid === uuid) tempType = item.itemTypeName
        return item.uuid !== uuid
      }),
    )

    setItemTypeExpanded(itemTypeExpanded.filter(item => item.uuid !== uuid))

    let tempItemListByType: ItemListData[] = []

    for (const type of itemListByType) {
      const y = type.items.filter(item => item.uuid !== uuid)
      tempItemListByType.push({ _id: type._id, itemTypeIcon: type.itemTypeIcon, items: y })
    }

    toast.info(`${tempType} ${t('removed-from-list')}`)
    setItemListByType(tempItemListByType)
  }

  const onScanToRemoveCancecl = () => {
    setShowRemoveScanner(false)
  }

  useEffect(() => {
    const onLoad = async () => {
      if (scannedItems.length < 1) {
        setActiveTab(1)
        setScannerActive(true)
      }
    }
    onLoad()
  }, [scannedItems])

  const onFinishScanning = () => {
    setShowReturnOverview(true)
  }

  const onConfirmAndSubmit = async () => {
    setIsLoading(true)
    if (scannedItems.length < 1) {
      toast.error(t('unknown-error-occured'))
      return
    }

    let itemIds: string = ''
    scannedItems.forEach(item => {
      itemIds += `"${item._id}",`
    })

    const mutation = `mutation {
        merchantResetItems(input: {
          ids: [${itemIds}]
      })
      {
          ids
      }
  }`

    const response = await wrappedExtendedMutateAuth(mutation)

    if (!response) {
      setEnded(true)
      toast.error(t('unknown-error-occured'))
      return
    }

    console.log(response)

    if (response.merchantResetItems.ids.length > 0) {
      setEnded(true)
      setFailedItems(response.merchantResetItems.ids)
      setShowFailedToReturnItems(true)
      return
    }

    toast.success(t('items-returned-to-inventory'))
    history.replace('/merchant/inventory')
  }

  return (
    <>
      {isLoading ? (
        <SplashScreen withFooter={false} />
      ) : showFailedToReturnItems ? (
        <main className="mainHome">
          <div className="screen-header">
            <img src={FailImage} alt="Red X" />
            <h1>{t('invalid-scans-return-to-inventory')}</h1>
          </div>
          <div className="items-overview">
            <div className="items-overview-header">
              <label>{t('item')}</label>
              <label>{t('serial-number')}</label>
            </div>
            <div className="items-overview-listed">
              {scannedItems.map(item => {
                return failedItems.length > 0 && failedItems.indexOf(item._id) > -1 ? (
                  <div key={item._id} className="items-overview-item-type">
                    <div className="items-overview-item-type-info">
                      <label>{t(item.itemTypeName)}</label>
                      <label>{item.serialNumber}</label>
                    </div>
                    <div className="custom-divider"></div>
                  </div>
                ) : null
              })}
            </div>
          </div>
          <div className="info-text-container">
            <div className="info-text-content">
              <label>{t('validation-failed-report')}</label>
            </div>
          </div>
          <div className="buttonDock-150"></div>
          <div className="floating-buttons">
            <button className="button-medium main-button" onClick={onHome}>
              {t('home')}
            </button>
          </div>
        </main>
      ) : showReturnOverview ? (
        <main className="mainHome">
          <div className="screen-header">
            <Link to="#" replace className="back" onClick={() => setShowReturnOverview(false)}>
              <ArrowLeftIcon className="bo-back" />
              {t('back')}
            </Link>
            <h1>{t('return-overview')}</h1>
          </div>
          <div className="items-overview">
            <div className="items-overview-header">
              <label>{t('item')}</label>
              <label>{t('quantity')}</label>
            </div>
            <div className="items-overview-listed">
              {itemListByType.map(type => (
                <div key={type._id} className="items-overview-item-type">
                  <div className="items-overview-item-type-info">
                    <label>{t(type.items[0].itemTypeName)}</label>
                    <label>{type.items.length}</label>
                  </div>
                  <div className="custom-divider"></div>
                </div>
              ))}
            </div>
            <div className="items-overview-totals">
              <label>{t('orders.total')}</label>
              <label>{scannedItems.length}</label>
            </div>
          </div>
          <div className="buttonDock-150"></div>
          <div className="floating-buttons">
            <button className="button-medium main-button" onClick={onConfirmAndSubmit}>
              {t('confirm-and-submit')}
            </button>
            <button className="button-medium naked-button" onClick={onCancel}>
              {t('cancel')}
            </button>
          </div>
        </main>
      ) : showRemoveScanner ? (
        <section className="qrScanner qr-scanner-expanded">
          <QrScannerExpanded callback={onScanToRemoveQRCode} callbackCancel={onScanToRemoveCancecl} heading={t('scan-remove')} text={t('scan-remove-text')} cancelBtnText={t('finish-removing')} cancelBtnClassName="circled-naked-button" />
        </section>
      ) : itemTypeExpanded.length > 0 ? (
        <main className="mainHome">
          <div className="screen-header">
            <Link to="#" replace className="back" onClick={() => setItemTypeExpanded([])}>
              <ArrowLeftIcon className="bo-back" />
              {t('back')}
            </Link>
            <div className="item-type-info">
              <img className="item-icon" alt="item-icon" src={itemTypeExpanded[0].iconUrl || t('no-image-available')} />
              <label>{itemTypeExpanded[0].itemTypeName}</label>
            </div>
          </div>
          <div className="scanned-items-container">
            <label>
              {itemTypeExpanded.length} {t('scanned-items')}
            </label>
            {itemTypeExpanded.map(item => (
              <div key={item.uuid} className="scanned-item-label">
                {item.serialNumber && (
                  <>
                    <div className="label-with-bin">
                      <label>{t('serial-number')}</label>
                      <BinIcon onClick={() => onClickToRemove(item.uuid)} />
                    </div>
                    <label>{item.serialNumber}</label>
                  </>
                )}
              </div>
            ))}
          </div>
        </main>
      ) : (
        <>
          {scannedItems.length < 1 ? (
            <></>
          ) : (
            <section className={`custom-tab-header ${scannerActive ? 'custom-tab-scanner-active' : ''}`}>
              <div className={`custom-tab ${activeTab === 1 ? 'active' : ''}`} onClick={onClickScanner}>
                <ScanIcon />
                <label>{t('scanner')}</label>
              </div>
              <div className={`custom-tab ${activeTab === 2 ? 'active' : ''}`} onClick={onClickList}>
                <ListIcon />
                <label>
                  {t('scanned-items')} ({scannedItems.length})
                </label>
              </div>
            </section>
          )}
          {activeTab === 1 && (
            <div key={isScanning} className={`custom-tab-panel ${activeTab === 1 && 'active-tab-panel'}`}>
              <section className="qrScanner qr-scanner-expanded">
                <QrScannerExpanded
                  callback={onQrCode}
                  callbackCancel={onCancel}
                  onContinueBtn={onFinishScanning}
                  heading={t('scan-containers')}
                  text={t('scan-containers-text')}
                  showContinueBtn={true}
                  continueBtnText={t('finish-scanning')}
                  className="tabbed-scanner"
                  continueBtnDisabled={scannedItems.length > 0 ? false : true}
                />
              </section>
            </div>
          )}
          {activeTab === 2 && (
            <div className={`custom-tab-panel ${activeTab === 2 && 'active-tab-panel'}`}>
              {lastScannedItem && (
                <div className="last-scanned-item">
                  <label>{t('last-scanned')}</label>
                  <div className="last-scanned-info-container">
                    <div className="last-scanned-item-type-info">
                      <img className="item-icon" alt="item-icon" src={lastScannedItem.iconUrl || t('no-image-available')} />
                      <label>{lastScannedItem.itemTypeName}</label>
                    </div>
                    <div className="last-scanned-info">
                      <div className="last-scanned-info-item">
                        {lastScannedItem.serialNumber && (
                          <>
                            <label>{t('serial-number')}</label>
                            <label>{lastScannedItem.serialNumber}</label>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="deposit-item-list">
                <div className="deposit-item-list-header">
                  <label>{t('item-list')}</label>
                  <label className="scan-to-remove" onClick={onScanToRemove}>
                    <BinIcon />
                    {t('scan-remove')}
                  </label>
                </div>
                <div className="items-categorized">
                  {itemListByType &&
                    itemListByType.length > 0 &&
                    itemListByType.map(
                      type =>
                        type.items &&
                        type.items.length > 0 && (
                          <div key={type.items[0].itemTypeName}>
                            <div className="categorized-item" onClick={() => onItemTypeClick(type.items)}>
                              <div className="categorized-item-info">
                                <div className="categorized-item-type-info">
                                  <img className="item-icon" alt="item-icon" src={type.itemTypeIcon || t('no-image-available')} />
                                  <label>{type.items[0].itemTypeName}</label>
                                </div>
                                <div className="item-type-count">
                                  <label>({type.items.length})</label>
                                  <ArrowRightIcon />
                                </div>
                              </div>
                            </div>
                            <div className="custom-divider"></div>
                          </div>
                        ),
                    )}
                </div>
              </div>
              <div className="buttonDock-150"></div>
              <div className="floating-buttons">
                <button className="button-medium main-button" onClick={onFinishScanning}>
                  {t('finish-scanning')}
                </button>
                <button className="button-medium naked-button" onClick={onCancel}>
                  {t('cancel')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}

export { ReturnToInventory as default }
